import { useCallback } from 'react'
import cx from 'classnames'
import { Modal } from 'almanac-ui/Modal'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import { Credits, EditContained, ChevronDown2, ChevronRightSmall } from 'blaze-ui/icons'
import { LoadingShimmerer } from 'components/LoadingShimmerer/LoadingShimmerer'
import { CampaignOption } from 'types'
import { CampaignChipDropdown } from './CampaignChipDropdown'
import Styles from './NewPostPicker.module.scss'

interface NewPostPickerProps {
  selectedSchedule?: CampaignOption
  schedules: CampaignOption[]
  isLoadingSchedules: boolean
  onScheduleChange: (id: number) => void
  onGenerateAi: (schedule?: CampaignOption) => void
  onBlankPost: (schedule?: CampaignOption) => void
}

interface OptionCardProps {
  tone: 'ai' | 'blank'
  icon: typeof Credits
  iconColor: string
  title: string
  subtitle: string
  onClick: () => void
}

const OptionCard = ({ tone, icon: Icon, iconColor, title, subtitle, onClick }: OptionCardProps) => {
  return (
    <button type="button" className={cx(Styles.option, Styles[tone])} onClick={onClick}>
      <Icon size={32} color={iconColor} />
      <div className={Styles.labels}>
        <Heading level={5}>{title}</Heading>
        <Text variant="secondary" color="var(--dark-60)">
          {subtitle}
        </Text>
      </div>
      <ChevronRightSmall size={12} />
    </button>
  )
}

export const NewPostPicker = ({
  selectedSchedule,
  schedules,
  isLoadingSchedules,
  onScheduleChange,
  onGenerateAi,
  onBlankPost,
}: NewPostPickerProps) => {
  const handleGenerateAi = useCallback(() => {
    onGenerateAi(selectedSchedule)
  }, [onGenerateAi, selectedSchedule])

  const handleBlankPost = useCallback(() => {
    onBlankPost(selectedSchedule)
  }, [onBlankPost, selectedSchedule])

  return (
    <>
      <Modal.Header>
        <div className={Styles.headerRow}>
          <Heading level={2}>New Post</Heading>
          <CampaignChipDropdown
            schedules={schedules}
            selectedSchedule={selectedSchedule}
            isLoading={isLoadingSchedules}
            onSelect={onScheduleChange}
          />
        </div>
      </Modal.Header>
      <Modal.Content withoutFooter className={Styles.content}>
        <div className={Styles.intro}>
          <Heading level={3}>Add your next post</Heading>
          <Heading level={4} color="var(--dark-60)">
            Add as many posts as you like. Upload your own media and write the caption, or let Blaze suggest a topic and
            reference image you can adjust before generating the post.
          </Heading>
        </div>
        <div className={Styles.options}>
          <OptionCard
            tone="ai"
            icon={Credits}
            iconColor="var(--growth)"
            title="Generate Post with AI"
            subtitle="Blaze will draft your content"
            onClick={handleGenerateAi}
          />
          <OptionCard
            tone="blank"
            icon={EditContained}
            iconColor="var(--green)"
            title="Add Blank Post"
            subtitle="Add your media & copy"
            onClick={handleBlankPost}
          />
        </div>
      </Modal.Content>
    </>
  )
}

export const NewPostPickerSkeleton = () => {
  return (
    <>
      <Modal.Header>
        <div className={Styles.headerRow}>
          <Heading level={2}>New Post</Heading>
          <div className={Styles.chipTrigger} aria-hidden>
            <Text variant="secondary" color="var(--dark-60)">
              Campaign
            </Text>
            <div className={Styles.chipBox}>
              <LoadingShimmerer className={Styles.skeletonChip} borderRadius={4} />
              <ChevronDown2 size={12} />
            </div>
          </div>
        </div>
      </Modal.Header>
      <Modal.Content withoutFooter className={Styles.content}>
        <div className={Styles.intro} aria-hidden>
          <LoadingShimmerer className={Styles.skeletonTitle} borderRadius={4} />
          <LoadingShimmerer className={Styles.skeletonSubtitle} borderRadius={4} />
          <LoadingShimmerer className={Styles.skeletonSubtitleShort} borderRadius={4} />
        </div>
        <div className={Styles.options} aria-hidden>
          <LoadingShimmerer className={Styles.optionSkeleton} borderRadius={10} />
          <LoadingShimmerer className={Styles.optionSkeleton} borderRadius={10} />
        </div>
      </Modal.Content>
    </>
  )
}
